import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { mutedColorFor, termStatusPresentation } from './termStatusPresentation';
import { colorPerceptuallyClose, readableTextColor } from './governanceThemeColors';
import { classifyEntityOwnership, EntityOwnership } from '../GovernanceAnalyticsAiAnswers.utils';

/**
 * One shared colour assignment per answer page: the own brand gets a stable colour and every
 * competitor gets a distinct colour from the palette, shared between the content and the sidebar.
 *
 * Every surface that names an entity — the answer tints, the legend, "Who's mentioned", the
 * attributes table and the cited-source attributions — resolves its colour through this one map,
 * so a competitor is the same colour wherever the reader meets it on the page.
 */

/** The minimum an entity has to expose to take part in the colour assignment. */
export interface ColorableEntity {
  name: string;
  type: TaxonomyFacet;
  /** Taxonomy term references; their keys are what the answer painter and attributes table key on. */
  terms?: readonly unknown[];
}

/**
 * A parsed-document mention: the extraction's own record of which entity it matched and which
 * taxonomy terms that match resolved to.
 */
export interface ColorableMention {
  entityName?: string;
  entityType?: TaxonomyFacet;
  terms?: readonly unknown[];
}

export interface EntityColorEntry {
  name: string;
  ownership: EntityOwnership;
  color: string;
  /** Taxonomy term keys that resolve to this entity's colour. */
  termKeys: string[];
}

export interface EntityColorMap {
  /** The colour every own-side entity shares — the theme primary. */
  ownColor: string;
  /** Taxonomy term key → colour. Used by the answer painter and the attributes table. */
  byTermKey: Map<string, string>;
  /** Entity name → colour. Used where only a name is available (citations, mention rows). */
  byName: Map<string, string>;
  /** One entry per distinct entity, in the order they were given (callers sort by share first). */
  entries: EntityColorEntry[];
  /** True when at least one own-side entity took part — drives the collapsed "You" legend chip. */
  hasOwn: boolean;
  /** Term keys whose colour is a stored brand colour — the ones a text site darkens for readability. */
  storedTermKeys: Set<string>;
  /** Entity names whose colour is a stored brand colour, for the sites that key on a name. */
  storedNames: Set<string>;
}

/** Reads a stable key off a runtime/serialized revision reference (or a bare string). */
function referenceKey(reference: unknown): string {
  if (typeof reference === 'string') {
    return reference.trim();
  }
  if (!reference || typeof reference !== 'object') {
    return '';
  }
  const record = reference as { getStableKey?: () => string; key?: unknown };
  return String(record.getStableKey?.() || record.key || '').trim();
}

/**
 * Every taxonomy term key an entity or a parsed mention references, deduplicated.
 *
 * The stored-colour lookup keys on these, derived from the same inputs `buildEntityColorMap`
 * receives rather than from the map it returns — feeding a colour map back through a hook keyed on
 * the map it produced would be circular.
 */
export function collectColorableTermKeys(
  entities: readonly ColorableEntity[] | undefined,
  mentions?: readonly ColorableMention[]
): string[] {
  const keys = new Set<string>();
  for (const entity of entities || []) {
    for (const term of entity.terms || []) {
      const key = referenceKey(term);
      if (key) {
        keys.add(key);
      }
    }
  }
  for (const mention of mentions || []) {
    for (const term of mention.terms || []) {
      const key = referenceKey(term);
      if (key) {
        keys.add(key);
      }
    }
  }
  return Array.from(keys);
}

/**
 * Assigns a colour to every entity on the page.
 *
 * All own-side entities share `ownColor`: the legend collapses them into a single "You" chip, so
 * giving them separate colours would contradict the legend. Competitors take their stored brand
 * colour when they have one and it does not collide, otherwise a successive colour from `palette`,
 * keyed by name — the same competitor mentioned twice, or reached through a different taxonomy
 * term, always lands on the colour it was first given. Entities that are neither own nor competitor
 * take no colour: tinting them would imply a tracking relationship the project has not declared.
 *
 * A stored colour is used only when it survives the collision check: not perceptually close to the
 * own colour (which would paint a rival in the customer's own hue and break the radar's separation),
 * and not close to a colour already assigned on this screen (which would make two entities read
 * alike). When it does not survive, the competitor falls back to the next free palette slot rather
 * than to a nudged hue nobody chose.
 */
export function buildEntityColorMap(
  entities: readonly ColorableEntity[] | undefined,
  ownColor: string,
  palette: readonly string[],
  mentions?: readonly ColorableMention[],
  storedColorByTermKey?: ReadonlyMap<string, string>
): EntityColorMap {
  const byTermKey = new Map<string, string>();
  const byName = new Map<string, string>();
  const entries: EntityColorEntry[] = [];
  const colorByCompetitorName = new Map<string, string>();
  // Every colour handed out so far — the own colour, then each competitor's — so a STORED colour can
  // be checked against all of them before it is used.
  const assignedColors: string[] = [ownColor];
  // Only the stored colours handed out, so the palette fallback can step past a slot a stored colour
  // already occupies without ever consulting the own colour.
  const storedColorsAssigned: string[] = [];
  let hasOwn = false;
  let competitorIndex = 0;

  const storedColorFor = (termKeys: readonly string[]): string | undefined => {
    if (!storedColorByTermKey) {
      return undefined;
    }
    for (const termKey of termKeys) {
      const stored = storedColorByTermKey.get(termKey);
      if (stored) {
        return stored;
      }
    }
    return undefined;
  };

  /**
   * The next palette colour, skipping only a slot a stored colour already occupies.
   *
   * A stored colour that survived the collision check can land on a palette slot and advances no
   * palette index, so the fallback steps past that slot — otherwise a later competitor could be
   * handed a colour a stored one already holds. It is checked against stored colours ALONE, never
   * the own colour or an earlier palette pick, so a tag with no stored colour keeps exactly today's
   * sequential assignment whatever the host theme's primary is. When every slot is taken (more
   * competitors than colours) it wraps, the only point two entities can share a colour.
   */
  const nextPaletteColor = (): string | undefined => {
    if (!palette.length) {
      return undefined;
    }
    for (let step = 0; step < palette.length; step += 1) {
      const candidate = palette[competitorIndex++ % palette.length];
      if (!storedColorsAssigned.some((stored) => colorPerceptuallyClose(stored, candidate))) {
        return candidate;
      }
    }
    return palette[competitorIndex++ % palette.length];
  };

  /** Assigns (or reuses) the colour for one entity, resolving its stored colour where term keys exist. */
  const colorFor = (name: string, ownership: EntityOwnership, termKeys: readonly string[]): string | undefined => {
    if (ownership === 'own') {
      hasOwn = true;
      return ownColor;
    }
    if (ownership !== 'competitor') {
      return undefined;
    }
    const existing = name ? colorByCompetitorName.get(name) : undefined;
    if (existing) {
      return existing;
    }

    const stored = storedColorFor(termKeys);
    // A stored colour is used only when it is distinct from the own colour and from every colour
    // already assigned — the check that keeps a rival out of the customer's own hue and off a
    // colour another entity holds.
    const usesStored = !!stored && !assignedColors.some((assigned) => colorPerceptuallyClose(assigned, stored));
    const color = usesStored ? stored : nextPaletteColor();

    if (usesStored && color) {
      storedColorsAssigned.push(color);
    }
    if (name && color) {
      colorByCompetitorName.set(name, color);
    }
    if (color) {
      assignedColors.push(color);
    }
    return color;
  };

  const storedTermKeys = new Set<string>();
  const storedNames = new Set<string>();

  const register = (name: string, ownership: EntityOwnership, color: string, termKeys: string[]) => {
    // A stored colour is one this run handed out from the stored value; only those are darkened for
    // readability at a text site, so a palette-coloured name stays exactly as it reads today.
    const isStored = storedColorsAssigned.includes(color);
    for (const termKey of termKeys) {
      if (!byTermKey.has(termKey)) {
        byTermKey.set(termKey, color);
      }
      if (isStored) {
        storedTermKeys.add(termKey);
      }
    }
    if (name && !byName.has(name)) {
      byName.set(name, color);
    }
    if (isStored && name) {
      storedNames.add(name);
    }
    const existing = entries.find((entry) => entry.name === name && entry.ownership === ownership);
    if (existing) {
      // Same entity reached through another source — widen its term coverage, keep one entry.
      existing.termKeys = Array.from(new Set([...existing.termKeys, ...termKeys]));
      return;
    }
    entries.push({ name, ownership, color, termKeys });
  };

  for (const entity of entities || []) {
    const ownership = classifyEntityOwnership(entity.type);
    const name = String(entity.name || '').trim();
    const termKeys = (entity.terms || []).map((term) => referenceKey(term)).filter((key) => !!key);
    const color = colorFor(name, ownership, termKeys);
    if (!color) {
      continue;
    }
    register(name, ownership, color, termKeys);
  }

  // `run.entities[].terms` is OPTIONAL on the entity contract, and the answer painter joins label
  // subjects to colours BY TERM KEY. When those terms are absent the map comes out empty and the
  // whole answer renders untinted even though the parsed document is complete — the regression this
  // guards. The parsed `mentions[]` carry the same association (entity name + the terms it matched),
  // so they are folded in as a second source: existing entities gain the term keys they were
  // missing, and an entity known only to the parse still gets a colour of the right family.
  for (const mention of mentions || []) {
    const name = String(mention.entityName || '').trim();
    const termKeys = (mention.terms || []).map((term) => referenceKey(term)).filter((key) => !!key);
    if (!name || !termKeys.length) {
      continue;
    }

    const knownColor = byName.get(name);
    if (knownColor) {
      const ownership = entries.find((entry) => entry.name === name)?.ownership ?? classifyEntityOwnership(mention.entityType);
      register(name, ownership, knownColor, termKeys);
      continue;
    }

    const ownership = classifyEntityOwnership(mention.entityType);
    const color = colorFor(name, ownership, termKeys);
    if (color) {
      register(name, ownership, color, termKeys);
    }
  }

  return { ownColor, byTermKey, byName, entries, hasOwn, storedTermKeys, storedNames };
}

/**
 * The colour to paint an entity's NAME in — the stored colour darkened to stay readable, or the
 * colour unchanged when it is a palette colour. Fills use the raw colour; only a stored colour a
 * person chose is darkened, so a tag without one reads exactly as it does today.
 */
export function readableEntityTextColor(
  colorMap: EntityColorMap,
  color: string | undefined,
  entity: { termKey?: string; name?: string }
): string | undefined {
  if (!color) {
    return color;
  }
  const isStored =
    (!!entity.termKey && colorMap.storedTermKeys.has(entity.termKey)) || (!!entity.name && colorMap.storedNames.has(entity.name));
  return isStored ? readableTextColor(color) : color;
}

/** One legend chip. Carries its term keys so the renderer can present the term's curation status. */
export interface LegendEntry {
  label: string;
  color: string;
  ownership: EntityOwnership;
  /** The taxonomy terms behind this chip; empty for the collapsed "You" chip. */
  termKeys: string[];
}

/**
 * The legend for an answer: a single "You" chip standing for every own-side entity, then one chip
 * per distinct competitor in the order they were assigned.
 */
export function buildEntityLegend(colorMap: EntityColorMap): LegendEntry[] {
  const legend: LegendEntry[] = [];

  if (colorMap.hasOwn) {
    // The "You" chip stands for every own-side entity at once, so no single term backs it.
    legend.push({ label: 'You', color: colorMap.ownColor, ownership: 'own', termKeys: [] });
  }

  const seen = new Set<string>();
  for (const entry of colorMap.entries) {
    if (entry.ownership !== 'competitor' || !entry.name || seen.has(entry.name)) {
      continue;
    }
    seen.add(entry.name);
    legend.push({ label: entry.name, color: entry.color, ownership: 'competitor', termKeys: entry.termKeys });
  }

  return legend;
}

/** The colour for an entity known only by name, or undefined when it is not colour-assigned. */
export function colorForEntityName(colorMap: EntityColorMap, name?: string): string | undefined {
  const trimmed = String(name || '').trim();
  return trimmed ? colorMap.byName.get(trimmed) : undefined;
}

/**
 * Mute the colours of entities whose term is a suggestion, and drop the ones whose term is archived.
 *
 * Applied to the whole colour map rather than at each paint site, because the answer-body tints are
 * keyed by term and painted far from any name — there is nowhere downstream to make the decision.
 * Entities with no resolved status keep their colour: a lookup failure must not silently change
 * how the answer is painted.
 */
export function applyTermStatusToColorMap(
  colorMap: EntityColorMap,
  statusByTermKey: Map<string, TaxonomyTermStatus | undefined>
): EntityColorMap {
  if (!statusByTermKey.size) {
    return colorMap;
  }

  const presentationFor = (termKeys: readonly string[]) => {
    // An entity can carry several term keys (a brand and its product). The first resolved status
    // decides, matching how the legend labels the entity.
    for (const termKey of termKeys) {
      const status = statusByTermKey.get(termKey);
      if (status) {
        return termStatusPresentation(status, 'chart');
      }
    }
    return undefined;
  };

  const entries: EntityColorEntry[] = [];
  const byTermKey = new Map<string, string>();
  const byName = new Map<string, string>();
  let hasOwn = false;

  for (const entry of colorMap.entries) {
    const presentation = presentationFor(entry.termKeys);
    if (presentation?.hidden) {
      continue;
    }

    const color = presentation ? (mutedColorFor(entry.color, presentation) ?? entry.color) : entry.color;
    entries.push({ ...entry, color });
    if (entry.ownership === 'own') {
      hasOwn = true;
    }
    for (const termKey of entry.termKeys) {
      byTermKey.set(termKey, color);
    }
    if (entry.name) {
      byName.set(entry.name, color);
    }
  }

  return { ...colorMap, entries, byTermKey, byName, hasOwn };
}
